import { Injectable, inject } from '@angular/core';

import { concatMap, map, mergeMap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { CategoryVppApplicationConfigurationService } from '@era/settings-sdk';
import { environment } from '@rocksoft/environment';
import { from } from 'rxjs';
import { compareVppApplications, safeMap } from '../effects.helpers';
import { getAncestors } from '../router/category';
import {
  CategoryVppApplicationsApiActions,
  CategoryVppApplicationsEffectActions,
  CategoryVppApplicationsResolverActions,
  CategoryVppApplicationsViewActions,
  DeviceConfiguredApplicationsResolverActions,
} from './category-vpp-applications.actions';
import { vppApplicationConfigurationFrom } from './category-vpp-applications.state';

@Injectable()
export class CategoryVppApplicationsEffects {
  private readonly actions = inject(Actions);
  private readonly categoryVppApplicationService = inject(CategoryVppApplicationConfigurationService);

  protected features = environment.features;

  /**
   * The category vpp application configuration has been updated.
   * Effect: we submit the update to the api and dispatch an error if applicable.
   */
  categoryVppApplicationLinkUpdatedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CategoryVppApplicationsViewActions.applicationConfigurationsUpdated),
      mergeMap((action) => {
        const categoryType = action.categoryType;

        return safeMap(
          this.categoryVppApplicationService.updateAllVppApplicationsForCategory({
            categoryType,
            categoryId: action.categoryId,
            operation: compareVppApplications(action),
          }),
          () =>
            CategoryVppApplicationsEffectActions.applicationsRequested({
              categoryId: action.categoryId,
              categoryType: action.categoryType,
            }),
          (error) =>
            CategoryVppApplicationsApiActions.applicationConfigurationsUpdatedError({
              categoryId: action.categoryId,
              categoryType: action.categoryType,
              error,
            })
        );
      })
    );
  });

  /**
   * The resolver has requested applications for a category.
   * Effect: we dispatch actions for the category and all its ancestors.
   */
  categoryVppApplicationsResolverEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CategoryVppApplicationsResolverActions.applicationsRequested),
      concatMap((action) => {
        const ancestors = getAncestors(action.category);
        return from(
          ancestors.map((ancestor) =>
            CategoryVppApplicationsEffectActions.applicationsRequested({
              categoryId: ancestor.categoryId,
              categoryType: ancestor.categoryType,
            })
          )
        );
      })
    );
  });

  /**
   * The (re)load of the category vpp applications has been requested.
   * Effect: we load the state from the api.
   */
  categoryVppApplicationsRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(
        DeviceConfiguredApplicationsResolverActions.vPPApplicationsRequested,
        CategoryVppApplicationsEffectActions.applicationsRequested
      ),
      mergeMap((action) => {
        const categoryType = action.categoryType;

        return safeMap(
          this.categoryVppApplicationService.getAllVppApplicationsForCategory({
            categoryType,
            categoryId: action.categoryId,
          }),
          (categoryVppApplications) =>
            CategoryVppApplicationsApiActions.applicationsLoaded({
              applications: categoryVppApplications.map(vppApplicationConfigurationFrom),
              categoryType: action.categoryType,
              categoryId: action.categoryId,
            }),
          (error) =>
            CategoryVppApplicationsApiActions.applicationsLoadedError({
              error,
            })
        );
      })
    );
  });

  /**
   * The vpp applications couldn't be updated.
   * Effect: we request a reload of the state from the api.
   */
  categoryVppApplicationsUpdatedErrorEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CategoryVppApplicationsApiActions.applicationConfigurationsUpdatedError),
      map((action) =>
        CategoryVppApplicationsEffectActions.applicationsRequested({
          categoryId: action.categoryId,
          categoryType: action.categoryType,
        })
      )
    );
  });
}
